import React from 'react';
import { useTranslation } from 'react-i18next';
import { Columns2, Rows2, Maximize2, Minimize2, X, Server, Terminal as TerminalIcon, RadioTower } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import TermIconButton from './TermIconButton';
import { chordKeys } from './terminalPaneShortcuts';
import { agentIconFor } from '../agents/agentIcons';

/** Tooltip text for a split button that cannot split right now. */
export function splitDisabledText(t, reason) {
  if (!reason) return null;
  if (reason === 'limit') return t('terminalExt.paneLimit', 'Up to 8 panes per tab');
  if (reason === 'small') return t('terminalExt.paneTooSmall', 'Not enough room to split this pane');
  if (reason === 'locked') return t('terminalExt.aiTabNoSplit', 'The AI tab runs approved commands only and cannot be split');
  return t('terminalExt.paneUnavailable', 'Not available');
}

export function statusDotClass(pane) {
  if (pane.status === 'exited') return pane.exitCode && pane.exitCode !== 0 ? 'exited-bad' : 'exited-ok';
  return pane.status || 'starting';
}

export function statusText(t, pane) {
  switch (pane.status) {
    case 'running':
      return t('terminalExt.statusRunning', 'Running');
    case 'exited':
      return t('terminalExt.statusExited', 'Exited');
    case 'error':
      return t('terminalExt.statusError', 'Failed to start');
    default:
      return t('terminalExt.statusStarting', 'Starting');
  }
}

const baseName = (p) => {
  if (!p) return '';
  const parts = String(p).replace(/[\\/]+$/, '').split(/[\\/]/);
  return parts[parts.length - 1] || p;
};

/**
 * The pane's icon: a built-in agent's brand icon (by `profileId`), a custom
 * profile's lettermark, or the shell/SSH icon.
 */
export function PaneIcon({ pane, size = 12 }) {
  const Brand = agentIconFor(pane.profileId);
  if (Brand) {
    return (
      <span className="term-agent-icon" data-agent-icon={pane.profileId} aria-hidden>
        <Brand width={size + 3} height={size + 3} focusable="false" />
      </span>
    );
  }
  if (pane.icon?.kind === 'lettermark') {
    return (
      <span className="term-lettermark" style={{ background: pane.icon.color }} aria-hidden>
        {pane.icon.text}
      </span>
    );
  }
  return (
    <span className="term-pane-icon" aria-hidden>
      {pane.kind === 'ssh' ? <Server size={size} /> : <TerminalIcon size={size} />}
    </span>
  );
}

/**
 * 26-px header inside the dark pane: status, icon, title, cwd, chips and the
 * pane actions (visible on hover and always on the focused pane; always
 * keyboard-reachable). Rendered only when it carries information — more than
 * one pane, an agent pane, or an exited pane.
 */
export default function TerminalPaneHeader({
  pane,
  title,
  zoomed,
  hiddenCount,
  broadcast,
  splitRow,
  splitCol,
  canZoom,
  chips,
  onSplit,
  onZoom,
  onClose,
  onContextMenu,
}) {
  const { t } = useTranslation();
  const cwd = pane.liveCwd || pane.cwd;
  return (
    <div className="term-pane-header" onContextMenu={onContextMenu}>
      <span
        className={`term-pane-dot term-pane-dot--${statusDotClass(pane)}`}
        role="img"
        aria-label={statusText(t, pane)}
      />
      <PaneIcon pane={pane} />
      <span className="term-pane-title">{title}</span>
      {cwd && (
        <Tooltip content={cwd} side="bottom">
          <span className="term-pane-cwd" tabIndex={-1}>
            · {baseName(cwd)}
          </span>
        </Tooltip>
      )}
      {broadcast && (
        <span className="term-pane-chip term-pane-chip--broadcast">
          <RadioTower size={10} aria-hidden />
          {t('terminalExt.broadcastChip', 'Broadcast')}
        </span>
      )}
      {zoomed && hiddenCount > 0 && (
        <span className="term-pane-chip">
          {t('terminalExt.zoomedHidden', 'Zoomed · {{count}} hidden', { count: hiddenCount })}
        </span>
      )}
      {chips}
      <span className="term-pane-spacer" />
      <div className="term-pane-actions">
        <TermIconButton
          label={t('terminalExt.splitRight', 'Split right')}
          keys={chordKeys('splitRight')}
          disabledReason={splitDisabledText(t, splitRow.reason)}
          onClick={(e) => onSplit('row', e.currentTarget)}
        >
          <Columns2 size={12} />
        </TermIconButton>
        <TermIconButton
          label={t('terminalExt.splitDown', 'Split down')}
          keys={chordKeys('splitDown')}
          disabledReason={splitDisabledText(t, splitCol.reason)}
          onClick={(e) => onSplit('col', e.currentTarget)}
        >
          <Rows2 size={12} />
        </TermIconButton>
        {canZoom && (
          <TermIconButton
            label={zoomed ? t('terminalExt.unzoomPane', 'Restore layout') : t('terminalExt.zoomPane', 'Zoom pane')}
            keys={chordKeys('zoom')}
            onClick={onZoom}
          >
            {zoomed ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
          </TermIconButton>
        )}
        <TermIconButton label={t('terminalExt.closePane', 'Close pane')} keys={chordKeys('close')} onClick={onClose}>
          <X size={12} />
        </TermIconButton>
      </div>
    </div>
  );
}
